/**
 * Product Stock Utility Functions
 *
 * Centralized functions for managing product stock operations including:
 * - CountedStock updates
 * - Stock adjustments
 * - Stock tracking
 *
 * This ensures consistency across all product stock operations in the application.
 */

import { doc, getDoc } from "firebase/firestore";
import { auth, firestore } from "../configs/FirebaseConfig";
import { FIRESTORE_PRODUCTS_STOCK_TABLE, FIRESTORE_REGIONS_TABLE } from "../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";
import dayjs from "dayjs";
import { updateCountedStock } from "./api/stock";

/**
 * Updates the countedStock for a product on today's date
 * Uses the stock API endpoint
 *
 * @param {string} productId - The product ID to update
 * @param {number} quantityDelta - The change in quantity (positive = increase, negative = decrease)
 * @param {boolean} trackAddedRemoved - Whether to track this in addedStock/removedStock (default: false for sold/picked stock)
 * @returns {Promise<void>}
 */
export const updateProductCountedStock = async (productId, quantityDelta, trackAddedRemoved = false) => {
  try {
    const dateId = dayjs().format("DDMMYYYY");
    const stockDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_STOCK_TABLE,
      dateId,
    );

    const stockDoc = await getDoc(stockDocRef);
    const currentCountedStock = Number(stockDoc.data()?.products?.[productId]?.countedStock || 0);
    const nextCountedStock = currentCountedStock + quantityDelta;

    // Get fresh ID token from current user
    if (!auth.currentUser) {
      throw new Error("No authenticated user");
    }
    const token = await auth.currentUser.getIdToken(true);

    await updateCountedStock(productId, dateId, nextCountedStock, COMPANY_COUNTRY_CODE, token, trackAddedRemoved);
  } catch (error) {
    console.error("Error updating countedStock for product", productId, error);
    throw error;
  }
};

/**
 * Updates countedStock for multiple products in a batch
 * Useful for bundle operations or bulk updates
 *
 * @param {Array<{productId: string, quantityDelta: number}>} updates - Array of product updates
 * @param {boolean} trackAddedRemoved - Whether to track in addedStock/removedStock
 * @returns {Promise<{success: boolean, updated: Array, failed: Array}>}
 */
export const updateMultipleProductCountedStock = async (updates, trackAddedRemoved = false) => {
  const results = {
    success: true,
    updated: [],
    failed: [],
  };

  for (const update of updates) {
    try {
      await updateProductCountedStock(update.productId, update.quantityDelta, trackAddedRemoved);
      results.updated.push(update.productId);
    } catch (error) {
      console.error(`Failed to update countedStock for product ${update.productId}:`, error);
      results.failed.push({
        productId: update.productId,
        error: error.message,
      });
      results.success = false;
    }
  }

  return results;
};

/**
 * Scanned bundle items without the bundle's crate (packagingMaterial).
 * The crate is already deducted via requestedStock when the order is created
 * (DS-465), so bundle assembly/unpick must not change its countedStock too.
 *
 * @param {Object<string, number>} scannedItems - productId -> scanned quantity
 * @param {string} bundleProductId
 * @param {Array} cart - order cart (packagingMaterial snapshot), falls back to products
 * @param {Array} products - product master
 */
export const omitBundlePackaging = (scannedItems, bundleProductId, cart, products) => {
  const packagingId = (
    (cart || []).find((p) => p?.productId === bundleProductId)?.packagingMaterial ||
    (products || []).find((p) => p?.productId === bundleProductId)?.packagingMaterial
  )?.productId;
  return Object.fromEntries(Object.entries(scannedItems || {}).filter(([productId]) => productId !== packagingId));
};

/**
 * Stock quantities per productId for an order cart, including the crates
 * (packagingMaterial) of bundles. Mirrors toStockLines in api stock.service.ts.
 *
 * @param {Array} cart - order userShoppingCart
 * @param {Map<string, Object>} productsByProductId - product master, for bundles without a snapshot
 * @returns {Map<string, {direct: number, viaBundle: number}>}
 */
export const cartStockLines = (cart, productsByProductId) => {
  const lines = new Map();
  const add = (productId, key, count) => {
    const line = lines.get(productId) || { direct: 0, viaBundle: 0 };
    line[key] += count;
    lines.set(productId, line);
  };
  for (const item of cart || []) {
    if (!item?.productId) continue;
    const itemCount = item.itemCount || 0;
    add(item.productId, "direct", itemCount);
    const packaging = item.packagingMaterial || productsByProductId.get(item.productId)?.packagingMaterial;
    if (packaging?.productId) add(packaging.productId, "viaBundle", (packaging.quantity || 1) * itemCount);
  }
  return lines;
};
